import { BottomAction } from '@euler/app/components/BottomAction';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { comparer, isNotNull } from '@euler/utils';
import { sorted, uniq } from '@euler/utils/array';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { memo } from 'react';
import { SectionList, SectionListData } from 'react-native';
import { runOnUI } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MemberPickerBottomSheet } from '../../../../../components/MemberPickerBottomSheet';
import * as InspectionSiteGrid from '../functions/InspectionSiteGrid';
import { useInspectionNavHeader } from '../functions/useInspectionNavHeader';
import {
  TaskInspectionProps,
  useTaskInspection,
} from '../functions/useTaskInspection';
import { SiteInspectionBottomSheetFlow } from '../site-inspection/SiteInspectionBottomSheetFlow';
import { HiddenSiteBottomSheet } from './HiddenSiteBottomSheet';
import { InspectionNavHeader } from './InspectionNavHeader';
import { InspectionSiteGridView } from './InspectionSiteGrid';
import { InspectionSiteGridHeader } from './InspectionSiteGridHeader';

export const InspectionScreenContent = memo(
  ({ flowId, onCancel }: TaskInspectionProps) => {
    const safeAreaInsets = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const { activeIndex, width, navHeaderHeight, setNavHeaderHeight } =
      useInspectionNavHeader();

    const {
      flow,
      sections,
      listRef,
      scrollY,
      isMemberListVisible,
      setIsMemberListVisible,
      scrollerItems,
      siteInspectionFlow,
      activeSectionForHiddenSites,
      setActiveSectionForHiddenSites,
      onSiteSelected,
      onSiteDismissed,
      onTechnicianPress,
      onMemberSelected,
      onMorePress,
      onHiddenSiteSelected,
      onScrollerItemPress,
      onSearchInputPress,
      onScroll,
      onPreview,
    } = useTaskInspection({ flowId, onCancel, mode: 'bottomSheet' });

    return (
      <>
        <InspectionNavHeader
          activeIndex={activeIndex}
          scrollerItems={scrollerItems}
          technicianName={flow?.assignedTo.name}
          onTechnicianPress={onTechnicianPress}
          onScrollerItemPress={onScrollerItemPress}
          onSearchInputPress={onSearchInputPress}
          onLayout={e => setNavHeaderHeight(e.nativeEvent.layout.height)}
          onMorePress={onMorePress}
        />
        <SectionList
          ref={listRef as any}
          contentContainerStyle={{
            paddingBottom: safeAreaInsets.bottom + rpx(15),
          }}
          onScroll={e => {
            runOnUI(onScroll)(e.nativeEvent.contentOffset.y);
          }}
          showsVerticalScrollIndicator={false}
          stickySectionHeadersEnabled={false}
          sections={sections}
          initialNumToRender={10}
          keyExtractor={x => x.groupId}
          viewabilityConfig={{
            itemVisiblePercentThreshold: 95,
            minimumViewTime: 50,
          }}
          renderSectionHeader={({
            section,
          }: {
            section: SectionListData<
              InspectionSiteGrid.GroupSectionDataItem,
              InspectionSiteGrid.GroupSection
            >;
          }) => {
            return (
              <InspectionSiteGridHeader
                width={width}
                groupId={section.groupId}
                name={section.title}
                hasMore={
                  section.hiddenSites != null && section.hiddenSites.length > 0
                }
                onMorePress={() => setActiveSectionForHiddenSites(section)}
              />
            );
          }}
          renderItem={({ item: section }) => (
            <InspectionSiteGridView
              items={section.items}
              width={width}
              onCellPress={onSiteSelected}
            />
          )}
          onViewableItemsChanged={({ viewableItems }) => {
            const visibleSectionIndics = sorted(
              uniq(
                viewableItems
                  .filter(x => isNotNull(x.section))
                  .map(x => {
                    const section =
                      x.section! as InspectionSiteGrid.GroupSection;
                    return section.index;
                  }),
                x => x,
              ),
              comparer,
            );
            activeIndex.value = visibleSectionIndics[0] ?? 0;
          }}
        />
        <BottomSheetModalProvider>
          <HiddenSiteBottomSheet
            show={activeSectionForHiddenSites != null}
            items={activeSectionForHiddenSites?.hiddenSites ?? []}
            topMargin={navHeaderHeight}
            onSelect={onHiddenSiteSelected}
            onDismiss={() => setActiveSectionForHiddenSites(undefined)}
          />
        </BottomSheetModalProvider>
        <SiteInspectionBottomSheetFlow
          ref={siteInspectionFlow}
          onDismiss={onSiteDismissed}
        />
        <BottomAction scrollY={scrollY}>
          <BottomAction.Button text="预览报告" onPress={onPreview} />
        </BottomAction>
        <MemberPickerBottomSheet
          show={isMemberListVisible}
          title={`${flow?.name} - 重新派工`}
          onSelect={onMemberSelected}
          onDismiss={() => setIsMemberListVisible(false)}
        />
      </>
    );
  },
);
